import React, { useEffect, useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { SERIES_FORMATS, TITLES, autoStatus, createChampionship, createEvent } from '../../services/tripleCrown';
import RoundEditor from './RoundEditor';
import { TZ, barbadosToUTC } from '../../utils/championshipDates';
import useRules from '../../hooks/useRules';
import { cleanTrackSettings } from './TrackSettings';

/* ══════════════════════════════════════
   CHAMPIONSHIP CREATE WIZARD
   ══════════════════════════════════════ */
export default function ChampionshipWizard({ onDone }) {
  const availableRules = useRules();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Step 1
  const [title, setTitle] = useState('');
  const [champStartDate, setChampStartDate] = useState('');
  const [champEndDate, setChampEndDate] = useState('');
  const [registrationStart, setRegistrationStart] = useState('');
  const [registrationEnd, setRegistrationEnd] = useState('');
  const regOrderBad = !!(registrationStart && registrationEnd && registrationEnd <= registrationStart);
  const [seriesFormat, setSeriesFormat] = useState('points');

  // Step 2
  const [rounds, setRounds] = useState([]);

  const addRound = () => {
    setRounds(prev => [...prev, {
      titleId: TITLES[0].id, scoringMode: 'time', name: '', description: '',
      ruleId: null, startDate: '', endDate: '', tracks: [], laps: null, carClass: null, tyre: null, trackSettings: [], carName: '', carImage: '', variables: [],
    }]);
  };

  const updateRound = (idx, round) => {
    setRounds(prev => { const next = [...prev]; next[idx] = round; return next; });
  };

  const removeRound = (idx) => {
    setRounds(prev => prev.filter((_, i) => i !== idx));
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const champStatus = autoStatus(champStartDate, champEndDate);
      const champId = await createChampionship({
        title: title.trim(),
        status: champStatus,
        seriesFormat,
        titles: [...new Set(rounds.map(r => r.titleId))],
        startDate: champStartDate ? Timestamp.fromDate(barbadosToUTC(champStartDate)) : null,
        endDate: champEndDate ? Timestamp.fromDate(barbadosToUTC(champEndDate)) : null,
        registrationStart: registrationStart ? Timestamp.fromDate(barbadosToUTC(registrationStart)) : null,
        registrationEnd: registrationEnd ? Timestamp.fromDate(barbadosToUTC(registrationEnd)) : null,
      });

      for (let i = 0; i < rounds.length; i++) {
        const rd = rounds[i];
        const titleObj = TITLES.find(t => t.id === rd.titleId);
        const roundStatus = autoStatus(rd.startDate, rd.endDate);
        await createEvent({
          championship: champId,
          titleId: rd.titleId,
          titleName: titleObj?.name || rd.titleId,
          phase: rd.scoringMode === 'time' ? 1 : 2,
          phaseName: rd.name?.trim() || `Round ${i + 1}`,
          description: rd.description?.trim() || '',
          ruleId: rd.ruleId || null,
          scoringMode: rd.scoringMode,
          tracks: rd.tracks || [],
          track: (rd.tracks || []).join(', '),
          laps: rd.laps || null,
          carClass: rd.titleId === 'granTurismo7' ? (rd.carClass || null) : null,
          tyre: rd.titleId === 'granTurismo7' ? (rd.tyre || null) : null,
          trackSettings: cleanTrackSettings(rd.tracks, rd.trackSettings, rd.titleId),
          carName: (rd.carName || '').trim() || null,
          carImage: rd.carImage || null,
          variables: rd.variables || [],
          startDate: rd.startDate ? Timestamp.fromDate(barbadosToUTC(rd.startDate)) : null,
          endDate: rd.endDate ? Timestamp.fromDate(barbadosToUTC(rd.endDate)) : null,
          scheduledDate: rd.startDate ? Timestamp.fromDate(barbadosToUTC(rd.startDate)) : null,
          status: roundStatus,
          livestreamUrl: '',
        });
      }

      setToast({ type: 'success', msg: `Championship created with ${rounds.length} round${rounds.length !== 1 ? 's' : ''}!` });
      setTimeout(() => onDone(), 1500);
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Failed to create championship' });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 4000); return () => clearTimeout(t); }
  }, [toast]);

  return (
    <div>
      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24 }}>
        {[1, 2, 3].map(s => (
          <div key={s} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: s <= step ? 'var(--teal)' : 'var(--border)',
            transition: 'background 0.3s',
          }} />
        ))}
      </div>

      {/* Step 1: Title, Dates & Format */}
      {step === 1 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Step 1 — Championship Details</h3>
          </div>
          <div className="form-group">
            <label className="form-label">Championship Name</label>
            <input className="form-input" value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Summer Invitational 2026" autoFocus />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input className="form-input" type="date" value={champStartDate}
                onChange={e => setChampStartDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">End Date</label>
              <input className="form-input" type="date" value={champEndDate}
                onChange={e => setChampEndDate(e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Registration Opens</label>
              <input className="form-input" type="datetime-local" value={registrationStart}
                onChange={e => setRegistrationStart(e.target.value)} />
              <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                Leave blank to allow registration immediately. If set, users cannot sign up until this date.
              </span>
            </div>
            <div className="form-group">
              <label className="form-label">Registration Closes</label>
              <input className="form-input" type="datetime-local" value={registrationEnd}
                onChange={e => setRegistrationEnd(e.target.value)} />
              <span style={{ fontSize: 11, color: regOrderBad ? 'var(--red, #ef4444)' : 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                {regOrderBad ? 'Must be after registration opens.' : 'Leave blank to keep registration open until the championship ends. After this, no new sign-ups.'}
              </span>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Series Format</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {SERIES_FORMATS.map(f => (
                <div
                  key={f.id}
                  onClick={() => setSeriesFormat(f.id)}
                  style={{
                    padding: '12px 14px', borderRadius: 8, cursor: 'pointer',
                    border: seriesFormat === f.id ? '2px solid var(--teal)' : '2px solid var(--border)',
                    background: seriesFormat === f.id ? 'var(--teal-soft)' : 'var(--bg-input)',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 13, color: seriesFormat === f.id ? 'var(--teal)' : 'var(--text)' }}>
                    {f.label}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {f.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
            <button className="btn btn-secondary" onClick={onDone}>Cancel</button>
            <button className="btn btn-primary" onClick={() => setStep(2)} disabled={!title.trim() || regOrderBad}>
              Next — Add Rounds
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Rounds */}
      {step === 2 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Step 2 — Rounds / Classes</h3>
            <button className="btn btn-primary btn-sm" onClick={addRound}>+ Add Round</button>
          </div>

          {rounds.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '32px 20px' }}>
              <p style={{ color: 'var(--text-muted)', marginBottom: 14 }}>
                No rounds added yet. Each round is a race event with its own game, tracks, and settings.
              </p>
              <button className="btn btn-primary" onClick={addRound}>+ Add First Round</button>
            </div>
          ) : (
            rounds.map((rd, i) => (
              <RoundEditor key={i} round={rd} index={i} onChange={updateRound} onRemove={removeRound} rules={availableRules} />
            ))
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
            <button className="btn btn-secondary" onClick={() => setStep(1)}>Back</button>
            <button className="btn btn-primary" onClick={() => setStep(3)} disabled={rounds.length === 0}>
              Next — Review
            </button>
          </div>
        </>
      )}

      {/* Step 3: Review */}
      {step === 3 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Step 3 — Review & Create</h3>
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{title}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {champStartDate || '—'} to {champEndDate || '—'}
              <span className={`badge ${autoStatus(champStartDate, champEndDate) === 'active' ? 'badge-success' : 'badge-muted'}`}
                style={{ marginLeft: 8 }}>{autoStatus(champStartDate, champEndDate)}</span>
              <span className="badge badge-teal" style={{ marginLeft: 6 }}>
                {SERIES_FORMATS.find(f => f.id === seriesFormat)?.label || seriesFormat}
              </span>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <strong style={{ fontSize: 14 }}>{rounds.length} Round{rounds.length !== 1 ? 's' : ''}</strong>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>#</th><th>Name</th><th>Game</th><th>Scoring</th><th>Tracks</th><th>Laps</th><th>Start</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rounds.map((rd, i) => {
                  const titleObj = TITLES.find(t => t.id === rd.titleId);
                  const status = autoStatus(rd.startDate, rd.endDate);
                  return (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{i + 1}</td>
                      <td>{rd.name || `Round ${i + 1}`}</td>
                      <td>{titleObj?.short || rd.titleId}</td>
                      <td><span className="badge badge-muted">{rd.scoringMode}</span></td>
                      <td>{(rd.tracks || []).length || '—'}</td>
                      <td>{rd.laps || '—'}</td>
                      <td style={{ fontSize: 12 }}>{rd.startDate ? new Date(rd.startDate).toLocaleDateString(undefined, { timeZone: TZ }) : '—'}</td>
                      <td><span className={`badge ${status === 'active' ? 'badge-success' : status === 'completed' ? 'badge-gold' : 'badge-muted'}`}>{status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
            <button className="btn btn-secondary" onClick={() => setStep(2)}>Back — Edit Rounds</button>
            <button className="btn btn-primary" onClick={handleCreate} disabled={saving}
              style={{ padding: '10px 28px' }}>
              {saving ? 'Creating...' : `Create Championship & ${rounds.length} Round${rounds.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}

      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}
